// src/pages/CustomerReport/AllCustomersReport.jsx
import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  MenuItem,
  Button,
  Grid,
  CircularProgress,
  Chip,
  Alert
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import axios from 'axios';
import { API_BASE_URL } from '../../constants/constants';
import BackButton from '../../components/BackButton';
import { usePermissions } from '../../context/PermissionContext';
import { customerNoOf } from '../../components/CustomerReportShell';


// Helper to normalize district names (Trivandrum <-> Thiruvananthapuram)
const normalizeDistrictName = (name) => {
  if (!name || name === 'N/A') return '';
  const n = String(name).trim().toLowerCase();
  if (n.includes('trivandrum') || n.includes('thiruvananthapuram') || n.includes('trivandram')) return 'thiruvananthapuram';
  if (n.includes('cochin') || n.includes('ernakulam')) return 'ernakulam';
  if (n.includes('calicut') || n.includes('kozhikode')) return 'kozhikode';
  if (n.includes('trichur') || n.includes('thrissur')) return 'thrissur';
  if (n.includes('palghat') || n.includes('palakkad')) return 'palakkad';
  if (n.includes('cannanore') || n.includes('kannur')) return 'kannur';
  if (n.includes('alleppey') || n.includes('alappuzha')) return 'alappuzha';
  if (n.includes('quilon') || n.includes('kollam')) return 'kollam';
  return n;
};

const AllCustomersReport = () => {
  const { can } = usePermissions();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [search, setSearch] = useState('');
  const [selectedState, setSelectedState] = useState('All');
  const [selectedDistrict, setSelectedDistrict] = useState('All');
  const [selectedTaluk, setSelectedTaluk] = useState('All');
  const [pincodeFilter, setPincodeFilter] = useState('');

  // Dropdown options
  const [districtList, setDistrictList] = useState([]);
  const [talukList, setTalukList] = useState([]);

  // Fetch Districts
  useEffect(() => {
    const fetchDistricts = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/districts`);
        if (res.status === 200) {
          const list = Array.isArray(res.data) ? res.data : res.data.districts || [];
          setDistrictList(list);
        }
      } catch (err) {
        console.error('Error fetching districts:', err);
      }
    };
    fetchDistricts();
  }, []);

  // Fetch Taluks when district changes
  useEffect(() => {
    const fetchTaluks = async () => {
      if (selectedDistrict === 'All') {
        setTalukList([]);
        setSelectedTaluk('All');
        return;
      }
      try {
        const distObj = districtList.find(d => (d.districtName || d.name) === selectedDistrict);
        const distId = distObj ? distObj._id : selectedDistrict;
        const res = await axios.get(`${API_BASE_URL}/taluks/district/${distId}`);
        if (res.status === 200) {
          const list = Array.isArray(res.data) ? res.data : res.data.taluks || [];
          setTalukList(list);
        }
      } catch (err) {
        console.error('Error fetching taluks:', err);
        setTalukList([]);
      }
    };
    fetchTaluks();
  }, [selectedDistrict, districtList]);

  // Fetch Customers
  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API_BASE_URL}/customerProfiles`, {
        params: {
          limit: 2000,
          level: 1
        }
      });
      if (res.status === 200) {
        const data = res.data.customers || res.data.data || [];
        setCustomers(data);
      }
    } catch (err) {
      console.error('Error fetching customers:', err);
      setError('Failed to fetch customers.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  // Apply Robust Frontend Filtering
  const filteredCustomers = customers.filter(c => {
    // District filter with normalization & fallback
    if (selectedDistrict !== 'All') {
      const targetNorm = normalizeDistrictName(selectedDistrict);
      const cNorm = normalizeDistrictName(c.customerDistrictName);
      let isDistMatch = false;

      if (cNorm && targetNorm && (cNorm === targetNorm || cNorm.includes(targetNorm) || targetNorm.includes(cNorm))) {
        isDistMatch = true;
      } else {
        const pin = Number(c.customerPincode);
        const taluk = (c.customerTalukName || '').toLowerCase();
        if (targetNorm === 'thiruvananthapuram') {
          if ((pin >= 695000 && pin <= 695999) || taluk.includes('thiruvananthapuram') || taluk.includes('trivandrum') || taluk.includes('neyyattinkara') || taluk.includes('nedumangad') || taluk.includes('attingal')) {
            isDistMatch = true;
          }
        }
      }

      if (!isDistMatch) return false;
    }

    // Village/Taluk filter
    if (selectedTaluk !== 'All') {
      const cTaluk = (c.customerTalukName || '').toLowerCase();
      if (!cTaluk.includes(selectedTaluk.toLowerCase())) return false;
    }

    // Pincode filter
    if (pincodeFilter.trim()) {
      const cPin = String(c.customerPincode || '');
      if (!cPin.includes(pincodeFilter.trim())) return false;
    }

    // Search by Name, Mobile, Customer ID (= Customer No), VPA
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      const cName = (c.customerName || '').toLowerCase();
      const cMobile = (c.customerMobile || '').toLowerCase();
      const fullId = String(c.customerId || c._id || '').toLowerCase();
      const shortId = String(c.customerNo || '').toLowerCase();
      const vpaStr = Array.isArray(c.customerVPA)
        ? c.customerVPA.map(v => (typeof v === 'string' ? v : (v?.vpa || ''))).join(' ').toLowerCase()
        : '';
      if (!cName.includes(q) && !cMobile.includes(q) && !(fullId && fullId === q) && !shortId.includes(q) && !vpaStr.includes(q)) {
        return false;
      }
    }
    return true;
  });

  // CSV Export (permission gated)
  const exportCSV = () => {
    if (!can('customers.all.export')) return;
    const headers = [
      'SL No',
      'Customer ID',
      'First Name',
      'Last Name',
      'Mobile Number',
      'List of Customer VPA ID',
      'Email',
      'STATE',
      'DISTRICT',
      'VILLAGE (CONSTITUENCY)',
      'Current Pincode'
    ];

    const rows = filteredCustomers.map((c, index) => {
      const nameParts = (c.customerName || '').trim().split(' ');
      const firstName = nameParts[0] || 'N/A';
      const lastName = nameParts.slice(1).join(' ') || 'N/A';
      const vpaList = Array.isArray(c.customerVPA) ? c.customerVPA.map(v => v.vpa || v).join('; ') : 'N/A';
      const formattedId = c.customerNo || '';
      const displayDist = (c.customerDistrictName && c.customerDistrictName !== 'N/A') ? c.customerDistrictName : 'Thiruvananthapuram';

      return [
        index + 1,
        `"${formattedId}"`,
        `"${firstName}"`,
        `"${lastName}"`,
        `"${c.customerMobile || ''}"`,
        `"${vpaList}"`,
        `"${c.customerEmail || 'N/A'}"`,
        '"Kerala"',
        `"${displayDist}"`,
        `"${c.customerTalukName || 'N/A'}"`,
        `"${c.customerPincode || ''}"`
      ];
    });

    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `All_Customers_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Box sx={{ p: 3, position: 'relative' }}>
      <BackButton />
      <br /><br />
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main', mb: 3 }}>
        All Customers (Super Admin)
      </Typography>

      {/* Filters & Export */}
      <Paper elevation={3} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Search Name / Mobile / ID / VPA"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              select
              size="small"
              label="State"
              value={selectedState}
              onChange={(e) => setSelectedState(e.target.value)}
            >
              <MenuItem value="All">All States</MenuItem>
              <MenuItem value="Kerala">Kerala</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              select
              size="small"
              label="District"
              value={selectedDistrict}
              onChange={(e) => setSelectedDistrict(e.target.value)}
            >
              <MenuItem value="All">All Districts</MenuItem>
              {districtList.map((d) => {
                const dName = d.districtName || d.name;
                return (
                  <MenuItem key={d._id || dName} value={dName}>
                    {dName}
                  </MenuItem>
                );
              })}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              select
              size="small"
              label="Village / Constituency"
              value={selectedTaluk}
              onChange={(e) => setSelectedTaluk(e.target.value)}
              disabled={selectedDistrict === 'All'}
            >
              <MenuItem value="All">All Villages / Taluks</MenuItem>
              {talukList.map((t) => {
                const tName = t.talukName || t.name;
                return (
                  <MenuItem key={t._id || tName} value={tName}>
                    {tName}
                  </MenuItem>
                );
              })}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Pincode"
              value={pincodeFilter}
              onChange={(e) => setPincodeFilter(e.target.value)}
            />
          </Grid>
          {can('customers.all.export') && (
            <Grid item xs={12} sm={6} md={1}>
              <Button
                fullWidth
                variant="contained"
                color="success"
                startIcon={<DownloadIcon />}
                onClick={exportCSV}
                sx={{ height: 40 }}
              >
                CSV
              </Button>
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* Table */}
      <TableContainer component={Paper} elevation={3} sx={{ borderRadius: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ p: 3 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        ) : (
          <Table>
            <TableHead sx={{ backgroundColor: 'primary.main' }}>
              <TableRow>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>SL No</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Customer ID</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>First Name</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Last Name</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Mobile Number</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>List of Customer VPA ID</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Email</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>STATE</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>DISTRICT</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>VILLAGE (CONSTITUENCY)</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Current Pincode</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredCustomers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} align="center">
                    No customers found matching criteria.
                  </TableCell>
                </TableRow>
              ) : (
                filteredCustomers.map((c, idx) => {
                  const nameParts = (c.customerName || '').trim().split(' ');
                  const firstName = nameParts[0] || 'N/A';
                  const lastName = nameParts.slice(1).join(' ') || 'N/A';
                  const vpaList = Array.isArray(c.customerVPA) && c.customerVPA.length > 0
                    ? c.customerVPA.map(v => v.vpa || v).join(', ')
                    : 'N/A';
                  const formattedId = customerNoOf(c);
                  const displayDist = (c.customerDistrictName && c.customerDistrictName !== 'N/A') ? c.customerDistrictName : 'Thiruvananthapuram';

                  return (
                    <TableRow key={c._id || idx} hover>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>{formattedId}</TableCell>
                      <TableCell>{firstName}</TableCell>
                      <TableCell>{lastName}</TableCell>
                      <TableCell>{c.customerMobile || 'N/A'}</TableCell>
                      <TableCell>
                        <Chip label={vpaList} size="small" variant="outlined" color="primary" />
                      </TableCell>
                      <TableCell>{c.customerEmail || 'N/A'}</TableCell>
                      <TableCell>Kerala</TableCell>
                      <TableCell>{displayDist}</TableCell>
                      <TableCell>{c.customerTalukName || 'N/A'}</TableCell>
                      <TableCell>{c.customerPincode || 'N/A'}</TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>
    </Box>
  );
};

export default AllCustomersReport;
